import { createRouter, createWebHistory } from 'vue-router/auto';
import { setupLayouts } from 'virtual:generated-layouts';
import type { Router } from 'vue-router/auto';
import { useClientStore, useMiscStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { RouteRecordRaw } from 'vue-router/auto';
import * as Sentry from '@sentry/vue';
import { get } from 'lodash';

let router: Router | undefined = undefined;

/**
 * Recursively apply layouts to all routes.
 *
 * This lets us use the layouts plugin to generate layouts for all routes, even nested ones. So we aren't forced to use the `parent.child` syntax.
 */
const recursiveLayouts = (route: RouteRecordRaw) => {
    if (!route.component && route.children) {
        for (let i = 0; i < route.children.length; i++) {
            route.children[i] = recursiveLayouts(route.children[i]);
        }

        return route;
    }

    return setupLayouts([route])[0];
};

export const getRouter = () => {
    if (router != null) {
        return router;
    }
    router = createRouter({
        history: createWebHistory(import.meta.env.BASE_URL),
        extendRoutes: routes => routes.map(recursiveLayouts),
    });

    const { isRouteLoading, routeVisiting } = storeToRefs(useMiscStore());

    router.beforeEach((to, from, next) => {
        isRouteLoading.value = true;
        routeVisiting.value = to.name;
        next();
        Sentry.setContext('route', {
            from: from.path,
            to: to.path,
            params: to.params,
            hash: to.hash,
            query: to.query,
            name: to.name,
            meta: to.meta,
        });
    });

    router.afterEach((to, from) => {
        if (routeVisiting.value === to.name) {
            routeVisiting.value = to.name;
        }
        isRouteLoading.value = false;
    });

    router.onError((error, to) => {
        const msg =
            typeof get(error, 'message') === 'string' ? error.message : typeof error === 'string' ? error : undefined;
        console.error(error, to, msg);
        if (
            msg != null &&
            (msg.includes('Failed to fetch dynamically imported module') ||
                msg.includes('Importing a module script failed') ||
                msg.includes('error loading dynamically imported module'))
        ) {
            if (!to?.fullPath) {
                window.location.reload();
            } else {
                window.location.href = to.fullPath;
            }
        }
    });

    return router;
};
